import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { Alert, Collapse, IconButton, Typography, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  dismissContentCard,
  trackContentCardClick,
  trackContentCardImpressions,
} from '@surfline/web-common';
import withContentCards from 'components/withContentCards';

import WavetrakLink from 'components/WavetrakLink';

import styles from './ContentCard.module.scss';

export interface ContentCardProps {
  card?: {
    name: string;
    extras?: {
      backgroundColor?: string;
      body?: string;
      closeButtonColor?: string;
      fontColor?: string;
      linkText?: string;
      linkUrl?: string;
    } | null;
  };
  className?: string;
  testId?: string;
}

export const ContentCard: React.FC<ContentCardProps> = ({
  card,
  className,
  testId = 'content-card',
}) => {
  const [closed, setClosed] = useState(false);
  const impressionTracked = useRef(false);
  const theme = useTheme();

  const cardName = card?.name ?? '';
  // Make sure we have defaults for all the properties
  const hasValidExtras = !!card?.extras?.body;
  const backgroundColor = card?.extras?.backgroundColor ?? theme.palette.background.default;
  const body = card?.extras?.body ?? '';
  const linkUrl = card?.extras?.linkUrl ?? '';
  const closeButtonColor = card?.extras?.closeButtonColor ?? theme.palette.secondary.main;
  const fontColor = card?.extras?.fontColor ?? theme.palette.secondary.main;
  const linkText = card?.extras?.linkText ?? '';

  // Track impressions once when card is available
  useEffect(() => {
    if (card && hasValidExtras && !impressionTracked.current) {
      impressionTracked.current = true;
      trackContentCardImpressions([card]);
    }
  }, [card, hasValidExtras]);

  // Component CTA was clicked
  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    if ((e.target as HTMLElement).closest('.close-icon') === null) {
      trackContentCardClick(card);
      if (linkUrl) {
        setTimeout(() => {
          window.location.assign(linkUrl);
        }, 200);
      }
    }
  };

  const handleClickAnchor = (e: React.MouseEvent<HTMLElement>) => {
    // let the main onclick handle the tracking and redirect
    e.preventDefault();
  };

  // Component was dismissed
  const handleCloseClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    setClosed(true);
    dismissContentCard(cardName);
  };

  // Only render the content if we have valid extras with a body
  if (!hasValidExtras) {
    return <div data-testid={testId} />;
  }

  return (
    <Collapse
      className={classNames(styles.contentCard, {
        [styles['contentCard--isLink']]: !!linkUrl,
      })}
      data-testid={testId}
      in={!closed}
    >
      <Alert
        action={
          <div className={styles.action}>
            <IconButton
              aria-label="close"
              color="inherit"
              data-testid="close-button"
              size="small"
              onClick={handleCloseClick}
            >
              <CloseIcon className="close-icon" fontSize="inherit" htmlColor={closeButtonColor} />
            </IconButton>
          </div>
        }
        icon={false}
        sx={{ backgroundColor }}
        className={classNames(styles.alert, className)}
        onClick={handleClick}
      >
        <Typography variant="subtitle1" component="span" sx={{ color: fontColor, fontWeight: 400 }}>
          {body}
        </Typography>{' '}
        {linkText && linkUrl && (
          <Typography
            className={styles.anchor}
            component={WavetrakLink}
            href={linkUrl}
            isExternal
            onClick={handleClickAnchor}
            sx={{ color: fontColor }}
            variant="subtitle1"
            data-testid={`${testId}-link`}
          >
            {linkText}
          </Typography>
        )}
      </Alert>
    </Collapse>
  );
};

export default withContentCards(ContentCard);
